import React from 'react';
import {
  makeStyles,
  createStyles,
  Theme,
  Button,
  ButtonProps,
} from '@material-ui/core';

const useStyles = makeStyles<Theme, { outline: boolean }>((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: ({ outline }) =>
        outline ? theme.palette.common.white : theme.palette.primary.main,
      border: ({ outline }) =>
        outline ? `1px solid ${theme.palette.secondary.main}` : 'none',
      borderRadius: '2px',
      padding: '8px 24px',
      textTransform: 'none',
      fontWeight: 700,
      pointerEvents: 'auto',
      color: ({ outline }) =>
        outline ? theme.palette.secondary.main : theme.palette.common.white,
      '&:hover': {
        backgroundColor: ({ outline }) =>
          outline ? theme.palette.primary.light : theme.palette.primary.dark,
      },
    },
    minWidth: {
      minWidth: 192,
    },
  })
);

/**
 * Button that can render as a Gatsby link when given a `to` prop
 *
 * Set `outline` prop to true to render the button with an "outline" variant.
 * Set `light` prop to true to render the button with a lighter background.
 */
const ButtonComponent: React.FC<
  {
    to?: string;
    outline?: boolean;
    light?: boolean;
    minWidth?: boolean;
    externalLink?: boolean;
  } & Partial<ButtonProps>
> = ({
  children,
  to = null,
  outline = false,
  minWidth = false,
  externalLink = false,
  ...rest
}) => {
  const classes = useStyles({ outline });
  const externalLinkProps = externalLink && {
    target: '_blank',
    rel: 'noopener noreferrer',
  };

  if (to) {
    return (
      <Button
        href={to}
        className={`${classes.root} ${minWidth ? classes.minWidth : ''}`}
        {...externalLinkProps}
        {...rest}
      >
        {children}
      </Button>
    );
  }

  return (
    <Button
      className={`${classes.root} ${minWidth ? classes.minWidth : ''}`}
      {...externalLinkProps}
      {...rest}
    >
      {children}
    </Button>
  );
};

export default ButtonComponent;
